import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useSiteContent } from "../../context/SiteContentContext";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, LogOut } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import ImageUploader from "../../components/admin/ImageUploader";

// --- Small reusable primitives ---------------------------------------------

function Field({ label, children }) {
    return (
        <div>
            <label className="tech-label mb-2 block">{label}</label>
            {children}
        </div>
    );
}

function ArrayEditor({ items, onChange, newItem, renderItem, addLabel = "Add item", testId }) {
    const update = (i, patch) => {
        const next = [...items];
        next[i] = { ...next[i], ...patch };
        onChange(next);
    };
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    const add = () => onChange([...items, typeof newItem === "function" ? newItem() : { ...newItem }]);

    return (
        <div className="space-y-4" data-testid={testId}>
            {items.map((item, i) => {
                // Prefer stable identifiers (id / n / slug) over array index so that
                // removing an item from the middle doesn't shift state from the next
                // sibling into this slot. Fall back to a composed key as last resort.
                const stableKey = item?.id || item?.slug || item?.n || `${testId}-${i}`;
                return (
                <div key={stableKey} className="tech-card !p-6 relative">
                    <div className="flex items-center justify-between mb-4">
                        <div className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-[var(--accent-indigo)]">
                            Item · {String(i + 1).padStart(2, "0")}
                        </div>
                        <button
                            type="button"
                            onClick={() => remove(i)}
                            className="text-white/50 hover:text-red-400"
                            data-testid={`${testId}-remove-${i}`}
                            aria-label="Remove"
                        >
                            <Trash2 size={15} />
                        </button>
                    </div>
                    {renderItem(item, (patch) => update(i, patch), i)}
                </div>
                );
            })}
            <button
                type="button"
                onClick={add}
                className="btn-secondary !py-2"
                data-testid={`${testId}-add`}
            >
                <Plus size={14} /> {addLabel}
            </button>
        </div>
    );
}

// Chip-list editor for simple string arrays (e.g. interests, deliverables).
function StringListEditor({ items, onChange, placeholder, testId }) {
    const [draft, setDraft] = useState("");
    return (
        <div data-testid={testId}>
            <div className="flex flex-wrap gap-2 mb-3">
                {items.map((t, i) => (
                    <span
                        key={`${t}-${i}`}
                        className="chip chip-active cursor-pointer hover:opacity-80"
                        onClick={() => onChange(items.filter((_, idx) => idx !== i))}
                        title="Click to remove"
                    >
                        {t} ×
                    </span>
                ))}
            </div>
            <div className="flex gap-3">
                <input
                    className="tech-input flex-1"
                    placeholder={placeholder}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && draft.trim()) {
                            e.preventDefault();
                            onChange([...items, draft.trim()]);
                            setDraft("");
                        }
                    }}
                    data-testid={`${testId}-input`}
                />
                <button
                    type="button"
                    className="btn-secondary !py-2"
                    onClick={() => {
                        if (draft.trim()) {
                            onChange([...items, draft.trim()]);
                            setDraft("");
                        }
                    }}
                    data-testid={`${testId}-add`}
                >
                    <Plus size={14} /> Add
                </button>
            </div>
        </div>
    );
}

// --- Section forms ---------------------------------------------------------

function SettingsForm({ value, onChange }) {
    const v = value || {};
    const set = (k) => (e) => onChange({ ...v, [k]: e.target.value });
    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Tagline line 1">
                    <input className="tech-input" value={v.tagline_line1 || ""} onChange={set("tagline_line1")} data-testid="settings-tagline1" />
                </Field>
                <Field label="Tagline line 2 (italic)">
                    <input className="tech-input" value={v.tagline_line2 || ""} onChange={set("tagline_line2")} data-testid="settings-tagline2" />
                </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Contact Email">
                    <input className="tech-input" value={v.contact_email || ""} onChange={set("contact_email")} data-testid="settings-email" />
                </Field>
                <Field label="Response Time">
                    <input className="tech-input" value={v.response_time || ""} onChange={set("response_time")} data-testid="settings-response" />
                </Field>
            </div>
            <Field label="Phone Numbers">
                <StringListEditor
                    items={v.phones || []}
                    onChange={(next) => onChange({ ...v, phones: next })}
                    placeholder="+91 7799100060 — press Enter to add"
                    testId="settings-phones"
                />
            </Field>
            <Field label="Corporate Office Address">
                <textarea rows={2} className="tech-input" value={v.corporate_address || ""} onChange={set("corporate_address")} data-testid="settings-corporate" />
            </Field>
            <Field label="Factory Address">
                <textarea rows={2} className="tech-input" value={v.factory_address || ""} onChange={set("factory_address")} data-testid="settings-factory" />
            </Field>
            <Field label="Default Social Share Image (og:image — 1200×630 recommended)">
                <ImageUploader
                    value={v.og_image_url || ""}
                    onChange={(url) => onChange({ ...v, og_image_url: url })}
                    testIdPrefix="settings-og-image"
                    helperText="Shown when visitors share any Skovomax link on LinkedIn, Slack, WhatsApp, etc. · 1200×630 recommended"
                />
            </Field>
            <Field label="Contact Form — Interest Options">
                <StringListEditor
                    items={v.interests || []}
                    onChange={(next) => onChange({ ...v, interests: next })}
                    placeholder="e.g. LiDAR Systems — press Enter to add"
                    testId="settings-interests"
                />
            </Field>
        </div>
    );
}

function HomeForm({ value, onChange }) {
    const v = value || {};
    const set = (k) => (e) => onChange({ ...v, [k]: e.target.value });
    return (
        <div className="space-y-6">
            <Field label="Hero eyebrow (tiny uppercase line above headline)">
                <input className="tech-input" value={v.hero_eyebrow || ""} onChange={set("hero_eyebrow")} data-testid="home-eyebrow" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Hero line 1">
                    <input className="tech-input" value={v.hero_line1 || ""} onChange={set("hero_line1")} data-testid="home-line1" />
                </Field>
                <Field label="Hero line 2 (italic)">
                    <input className="tech-input" value={v.hero_line2 || ""} onChange={set("hero_line2")} data-testid="home-line2" />
                </Field>
            </div>
            <Field label="Hero body">
                <textarea rows={3} className="tech-input" value={v.hero_body || ""} onChange={set("hero_body")} data-testid="home-body" />
            </Field>

            <Field label="Hero meta readout (label / value)">
                <ArrayEditor
                    items={v.hero_meta || []}
                    onChange={(next) => onChange({ ...v, hero_meta: next })}
                    newItem={{ label: "", value: "" }}
                    addLabel="Add meta item"
                    testId="home-meta"
                    renderItem={(item, upd, i) => (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input className="tech-input" placeholder="LABEL" value={item.label} onChange={(e) => upd({ label: e.target.value })} data-testid={`home-meta-label-${i}`} />
                            <input className="tech-input" placeholder="Value" value={item.value} onChange={(e) => upd({ value: e.target.value })} data-testid={`home-meta-value-${i}`} />
                        </div>
                    )}
                />
            </Field>

            <Field label="Stats (the four big numbers under the hero)">
                <ArrayEditor
                    items={v.stats || []}
                    onChange={(next) => onChange({ ...v, stats: next })}
                    newItem={{ value: "", label: "", sub: "" }}
                    addLabel="Add stat"
                    testId="home-stats"
                    renderItem={(item, upd, i) => (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <input className="tech-input" placeholder="97%" value={item.value} onChange={(e) => upd({ value: e.target.value })} data-testid={`home-stat-value-${i}`} />
                            <input className="tech-input" placeholder="Flood Prediction Accuracy" value={item.label} onChange={(e) => upd({ label: e.target.value })} data-testid={`home-stat-label-${i}`} />
                            <input className="tech-input" placeholder="Ensemble ML · U.S. ACE" value={item.sub} onChange={(e) => upd({ sub: e.target.value })} data-testid={`home-stat-sub-${i}`} />
                        </div>
                    )}
                />
            </Field>
        </div>
    );
}

function CaseStudiesForm({ value, onChange }) {
    return (
        <ArrayEditor
            items={value || []}
            onChange={onChange}
            newItem={() => ({ id: Math.random().toString(36).slice(2, 8), slug: "", tag: "", client: "", title: "", body: "", long_body: "", metric: "", metric_label: "", partners: [], outcomes: [] })}
            addLabel="Add case study"
            testId="cases"
            renderItem={(item, upd, i) => (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Tag (e.g. GeoAI · Hazard)">
                            <input className="tech-input" value={item.tag || ""} onChange={(e) => upd({ tag: e.target.value })} data-testid={`case-tag-${i}`} />
                        </Field>
                        <Field label="Client">
                            <input className="tech-input" value={item.client || ""} onChange={(e) => upd({ client: e.target.value })} data-testid={`case-client-${i}`} />
                        </Field>
                    </div>
                    <Field label="Title">
                        <input className="tech-input" value={item.title || ""} onChange={(e) => upd({ title: e.target.value })} data-testid={`case-title-${i}`} />
                    </Field>
                    <Field label="Slug (URL path — auto-generated from title if blank)">
                        <input className="tech-input" placeholder="flood-modeling-ensemble-ml" value={item.slug || ""} onChange={(e) => upd({ slug: e.target.value })} data-testid={`case-slug-${i}`} />
                    </Field>
                    <Field label="Body / Summary (shown on card + as pull-quote on detail page)">
                        <textarea rows={4} className="tech-input" value={item.body || ""} onChange={(e) => upd({ body: e.target.value })} data-testid={`case-body-${i}`} />
                    </Field>
                    <Field label="Long body (Markdown — shown only on the detail page. Optional.)">
                        <textarea rows={10} className="tech-input" placeholder="## Context&#10;...&#10;&#10;## Approach&#10;..." value={item.long_body || ""} onChange={(e) => upd({ long_body: e.target.value })} data-testid={`case-long-body-${i}`} />
                    </Field>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Metric (e.g. 97%)">
                            <input className="tech-input" value={item.metric || ""} onChange={(e) => upd({ metric: e.target.value })} data-testid={`case-metric-${i}`} />
                        </Field>
                        <Field label="Metric label (e.g. accuracy)">
                            <input className="tech-input" value={item.metric_label || ""} onChange={(e) => upd({ metric_label: e.target.value })} data-testid={`case-metric-label-${i}`} />
                        </Field>
                    </div>
                    <Field label="Partners">
                        <StringListEditor
                            items={item.partners || []}
                            onChange={(next) => upd({ partners: next })}
                            placeholder="e.g. NASA JPL"
                        />
                    </Field>
                    <Field label="Outcomes">
                        <StringListEditor
                            items={item.outcomes || []}
                            onChange={(next) => upd({ outcomes: next })}
                            placeholder="e.g. 97% AUC on held-out flood inventory"
                        />
                    </Field>
                </div>
            )}
        />
    );
}

function ServicesForm({ value, onChange }) {
    return (
        <ArrayEditor
            items={value || []}
            onChange={onChange}
            newItem={() => ({ n: String((value?.length || 0) + 1).padStart(2, "0"), title: "", body: "", deliverables: [] })}
            addLabel="Add service"
            testId="services"
            renderItem={(item, upd, i) => (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label="Number">
                            <input className="tech-input" value={item.n || ""} onChange={(e) => upd({ n: e.target.value })} data-testid={`service-n-${i}`} />
                        </Field>
                        <div className="md:col-span-2">
                            <Field label="Title">
                                <input className="tech-input" value={item.title || ""} onChange={(e) => upd({ title: e.target.value })} data-testid={`service-title-${i}`} />
                            </Field>
                        </div>
                    </div>
                    <Field label="Body">
                        <textarea rows={3} className="tech-input" value={item.body || ""} onChange={(e) => upd({ body: e.target.value })} data-testid={`service-body-${i}`} />
                    </Field>
                    <Field label="Deliverables">
                        <StringListEditor
                            items={item.deliverables || []}
                            onChange={(next) => upd({ deliverables: next })}
                            placeholder="e.g. Validation report — press Enter to add"
                            testId={`service-deliverables-${i}`}
                        />
                    </Field>
                </div>
            )}
        />
    );
}

function ApplicationsForm({ value, onChange }) {
    return (
        <ArrayEditor
            items={value || []}
            onChange={onChange}
            newItem={() => ({ n: String((value?.length || 0) + 1).padStart(2, "0"), tag: "", title: "", body: "" })}
            addLabel="Add application"
            testId="apps"
            renderItem={(item, upd, i) => (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label="Number">
                            <input className="tech-input" value={item.n || ""} onChange={(e) => upd({ n: e.target.value })} data-testid={`app-n-${i}`} />
                        </Field>
                        <Field label="Tag (e.g. Climate)">
                            <input className="tech-input" value={item.tag || ""} onChange={(e) => upd({ tag: e.target.value })} data-testid={`app-tag-${i}`} />
                        </Field>
                        <Field label="Title">
                            <input className="tech-input" value={item.title || ""} onChange={(e) => upd({ title: e.target.value })} data-testid={`app-title-${i}`} />
                        </Field>
                    </div>
                    <Field label="Body">
                        <textarea rows={3} className="tech-input" value={item.body || ""} onChange={(e) => upd({ body: e.target.value })} data-testid={`app-body-${i}`} />
                    </Field>
                </div>
            )}
        />
    );
}

function PrinciplesForm({ value, onChange }) {
    return (
        <ArrayEditor
            items={value || []}
            onChange={onChange}
            newItem={() => ({ n: String((value?.length || 0) + 1).padStart(2, "0"), title: "", body: "" })}
            addLabel="Add principle"
            testId="principles"
            renderItem={(item, upd, i) => (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label="Number">
                            <input className="tech-input" value={item.n || ""} onChange={(e) => upd({ n: e.target.value })} data-testid={`pri-n-${i}`} />
                        </Field>
                        <div className="md:col-span-2">
                            <Field label="Title">
                                <input className="tech-input" value={item.title || ""} onChange={(e) => upd({ title: e.target.value })} data-testid={`pri-title-${i}`} />
                            </Field>
                        </div>
                    </div>
                    <Field label="Body">
                        <textarea rows={3} className="tech-input" value={item.body || ""} onChange={(e) => upd({ body: e.target.value })} data-testid={`pri-body-${i}`} />
                    </Field>
                </div>
            )}
        />
    );
}

// --- Main page --------------------------------------------------------------

const TABS = [
    { key: "settings", label: "Settings", description: "Contact, coordinates, tagline, interests list." },
    { key: "home", label: "Home", description: "Hero text, meta readout, and stats." },
    { key: "case_studies", label: "Case Studies", description: "Cards on the Case Studies page (first 4 also appear on Home)." },
    { key: "services", label: "Services", description: "Services page cards + deliverables." },
    { key: "applications", label: "Applications", description: "Applications page grid." },
    { key: "about_principles", label: "About Principles", description: "Principle cards on About." },
];

export default function ContentEditor() {
    const { content, refresh, setContent } = useSiteContent();
    const { logout } = useAuth();
    const nav = useNavigate();
    const [active, setActive] = useState("settings");
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (content) setDraft(content);
    }, [content]);

    const currentTab = TABS.find((t) => t.key === active);
    const value = draft?.[active];

    const onChange = (next) => {
        setDraft((d) => ({ ...d, [active]: next }));
    };

    const onSave = async () => {
        if (!value) return;
        setSaving(true);
        try {
            const { data } = await api.put(`/admin/site-content/${active}`, { data: value });
            setContent(data);
            setDraft(data);
            toast.success(`Saved: ${currentTab.label}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const onReset = () => {
        if (!content) return;
        setDraft(content);
        toast.message("Changes reverted.");
    };

    const onLogout = async () => {
        await logout();
        nav("/admin/login");
    };

    if (!draft) {
        return <div className="min-h-screen bg-[#050510] text-white p-12 tech-label">Loading content…</div>;
    }

    return (
        <div className="min-h-screen bg-[#050510] text-white">
            <header className="glass-nav fixed top-0 inset-x-0 z-30 py-3">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/admin" className="inline-flex items-center gap-2 tech-label hover:text-white">
                            <ArrowLeft size={14} /> Admin
                        </Link>
                        <div className="font-serif-display text-xl">Site Content</div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onReset}
                            className="btn-secondary !py-2 !px-4"
                            data-testid="content-reset"
                        >
                            Revert
                        </button>
                        <button
                            onClick={onSave}
                            className="btn-primary !py-2 !px-4"
                            disabled={saving}
                            data-testid="content-save"
                        >
                            <Save size={14} /> {saving ? "Saving…" : `Save ${currentTab.label}`}
                        </button>
                        <button onClick={onLogout} className="btn-secondary !py-2 !px-4" data-testid="content-logout">
                            <LogOut size={14} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="pt-24 pb-20 max-w-[1440px] mx-auto px-6 md:px-10">
                <div className="overline mb-3">Operations · Content</div>
                <h1 className="font-serif-display text-5xl md:text-6xl leading-none mb-8">Edit Site Content</h1>

                <div className="flex flex-wrap gap-3 mb-6">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setActive(t.key)}
                            className={`chip ${active === t.key ? "chip-active" : ""}`}
                            data-testid={`content-tab-${t.key}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="tech-card !p-8 mb-6">
                    <div className="overline mb-2">{currentTab.label}</div>
                    <p className="text-white/55 text-sm">{currentTab.description}</p>
                </div>

                <div className="tech-card !p-8" data-testid={`content-form-${active}`}>
                    {active === "settings" && <SettingsForm value={value} onChange={onChange} />}
                    {active === "home" && <HomeForm value={value} onChange={onChange} />}
                    {active === "case_studies" && <CaseStudiesForm value={value} onChange={onChange} />}
                    {active === "services" && <ServicesForm value={value} onChange={onChange} />}
                    {active === "applications" && <ApplicationsForm value={value} onChange={onChange} />}
                    {active === "about_principles" && <PrinciplesForm value={value} onChange={onChange} />}
                </div>

                <div className="mt-8 flex justify-end">
                    <button onClick={onSave} className="btn-primary" disabled={saving} data-testid="content-save-bottom">
                        <Save size={14} /> {saving ? "Saving…" : `Save ${currentTab.label}`}
                    </button>
                </div>
            </main>
        </div>
    );
}
